import { useEffect, useState } from "react";
import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import Input, { InputType } from "components/shared/inputs/input";
import Select from "components/shared/inputs/select";
import { env } from "env";
import { getLocalStorageAccessToken } from "utils/helpers/localStorage";
import useTranslation from "utils/hooks/useTranslation";

type DataSourceRecord = Record<string, any>;

type CrudSection = {
  active: boolean;
  endpoint: string;
  method: string;
};

type DistantSection = {
  active: boolean;
  limit: string;
  skip: string;
  sort: string;
  where: string;
  total: string;
};

export type DatasourceFormState = {
  name: string;
  baseUrl: string;
  format: string;
  type: string;
  read: {
    endpoint: string;
    method: string;
  };
  create: CrudSection;
  update: CrudSection;
  trash: CrudSection;
  distant: DistantSection;
};

const initialFormState: DatasourceFormState = {
  name: "",
  baseUrl: "",
  format: "JSON",
  type: "REST",
  read: {
    endpoint: "",
    method: "GET",
  },
  create: {
    active: false,
    endpoint: "",
    method: "POST",
  },
  update: {
    active: false,
    endpoint: "/##id##",
    method: "PUT",
  },
  trash: {
    active: false,
    endpoint: "/##id##",
    method: "DELETE",
  },
  distant: {
    active: false,
    limit: "",
    skip: "",
    sort: "",
    where: "",
    total: "",
  },
};

function toForm(row: DataSourceRecord | null): DatasourceFormState {
  if (!row) return initialFormState;

  return {
    name: String(row.name ?? ""),
    baseUrl: String(row.baseUrl ?? ""),
    format: String(row.format ?? "JSON"),
    type: String(row.type ?? "REST"),
    read: {
      endpoint: String(row.read?.endpoint ?? ""),
      method: String(row.read?.method ?? "GET"),
    },
    create: {
      active: Boolean(row.create?.active),
      endpoint: String(row.create?.endpoint ?? ""),
      method: String(row.create?.method ?? "POST"),
    },
    update: {
      active: Boolean(row.update?.active),
      endpoint: String(row.update?.endpoint ?? "/##id##"),
      method: String(row.update?.method ?? "PUT"),
    },
    trash: {
      active: Boolean(row.trash?.active),
      endpoint: String(row.trash?.endpoint ?? "/##id##"),
      method: String(row.trash?.method ?? "DELETE"),
    },
    distant: {
      active: Boolean(row.distant?.active),
      limit: String(row.distant?.limit ?? ""),
      skip: String(row.distant?.skip ?? ""),
      sort: String(row.distant?.sort ?? ""),
      where: String(row.distant?.where ?? ""),
      total: String(row.distant?.total ?? ""),
    },
  };
}

type Props = {
  isOpen: boolean;
  editingRow: DataSourceRecord | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void> | void;
};

const DatasourceEditorPopup: React.FC<Props> = ({ isOpen, editingRow, isSaving, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<DatasourceFormState>(initialFormState);
  const [isTesting, setIsTesting] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [testResult, setTestResult] = useState<unknown>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(toForm(editingRow));
    setIsTesting(false);
    setShowTest(false);
    setTestResult(null);
  }, [isOpen, editingRow]);

  const setTop = (field: "name" | "baseUrl" | "format" | "type", value: string) => {
    setForm((c) => ({ ...c, [field]: value }));
  };

  const setRead = (field: "endpoint" | "method", value: string) => {
    setForm((c) => ({ ...c, read: { ...c.read, [field]: value } }));
  };

  const toggleSection = (section: "create" | "update" | "trash" | "distant") => {
    setForm((c) => ({
      ...c,
      [section]: { ...c[section], active: !c[section].active },
    }));
  };

  const setCrud = (section: "create" | "update" | "trash", field: "method" | "endpoint", value: string) => {
    setForm((c) => ({ ...c, [section]: { ...c[section], [field]: value } }));
  };

  const setDistant = (field: "limit" | "skip" | "sort" | "where" | "total", value: string) => {
    setForm((c) => ({ ...c, distant: { ...c.distant, [field]: value } }));
  };

  const onTestRead = async () => {
    setShowTest(true);
    const baseUrl = form.baseUrl.trim();
    if (!baseUrl) {
      setTestResult(t("data_not_found"));
      return;
    }

    setIsTesting(true);
    try {
      const isAbsoluteBaseUrl = /^https?:\/\//i.test(baseUrl);
      const relativeBasePrefix = baseUrl.startsWith("/") ? "" : "/";
      const origin = String(env.api_url ?? "").replace(/\/+$/, "");
      const normalizedBaseUrl = isAbsoluteBaseUrl
        ? baseUrl
        : `${origin}${relativeBasePrefix}${baseUrl}`;
      const normalizedBaseWithoutTrailingSlash = normalizedBaseUrl.replace(/\/+$/, "");
      const readEndpoint = form.read.endpoint.trim();
      let normalizedReadEndpoint = "";
      if (readEndpoint) {
        normalizedReadEndpoint = readEndpoint.startsWith("/")
          ? readEndpoint
          : `/${readEndpoint}`;
      }
      const endpoint = `${normalizedBaseWithoutTrailingSlash}${normalizedReadEndpoint}`;
      const token = getLocalStorageAccessToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        method: form.read.method || "GET",
        headers,
        credentials: "include",
      });
      const ct = (response.headers.get("content-type") ?? "").toLowerCase();

      if (!response.ok) {
        if (response.status === 401) {
          setTestResult("Unauthorized (401). Check login/token or API permissions.");
          return;
        }
        setTestResult(`Request failed with status ${response.status} ${response.statusText}`);
        return;
      }

      if (ct.includes("text/html")) {
        setTestResult("Invalid API response (HTML). Check Base URL and endpoint.");
        return;
      }

      setTestResult(ct.includes("application/json") ? await response.json() : await response.text());
    } catch (error) {
      setTestResult(error instanceof Error ? error.message : "get_error");
    } finally {
      setIsTesting(false);
    }
  };

  const onSubmit = async () => {
    await onSave({
      name: form.name.trim(),
      type: form.type.trim(),
      format: form.format.trim(),
      baseUrl: form.baseUrl.trim(),
      read: {
        endpoint: form.read.endpoint.trim(),
        method: form.read.method.trim() || "GET",
      },
      create: {
        active: form.create.active,
        endpoint: form.create.endpoint.trim(),
        method: form.create.method.trim(),
      },
      update: {
        active: form.update.active,
        endpoint: form.update.endpoint.trim(),
        method: form.update.method.trim(),
      },
      trash: {
        active: form.trash.active,
        endpoint: form.trash.endpoint.trim(),
        method: form.trash.method.trim(),
      },
      distant: {
        active: form.distant.active,
        limit: form.distant.limit.trim(),
        skip: form.distant.skip.trim(),
        sort: form.distant.sort.trim(),
        where: form.distant.where.trim(),
        total: form.distant.total.trim(),
      },
    });
  };

  let testResultValue = "";
  if (isTesting) {
    testResultValue = "...";
  } else if (typeof testResult === "string") {
    testResultValue = testResult;
  } else {
    testResultValue = JSON.stringify(testResult ?? "", null, 2);
  }

  if (!isOpen) return null;

  return (
    <Popup
      title={editingRow ? "Modifier datasource" : "Nouveau datasource"}
      onClose={onClose}
      portal
      fixSize="large"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>{t("Back")}</Button>
            <Button type="button" primary onClick={onSubmit} disabled={isSaving}>{t(editingRow ? "Update" : "Add")}</Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="20" data-space="40" data-space-horizontal="40" data-space-top="5" data-height="autofit">
        {/* <text data-color="text-secondary-color">{t("intro_add_datasource")}</text> */}

        <group data-direction="column" data-gap="12">
          <group data-gap="20">
            <group data-fit="1" data-length="forcefit">
              <Input dataLength="autofit" size="large" type="text" label="Name" name="ds_name" required value={form.name} onChange={(e: any) => setTop("name", String(e.target?.value ?? ""))} />
            </group>
            <group data-fit="1" data-length="forcefit">
              <Select dataLength="autofit" size="large" label={t("type_interface")} name="ds_type" required value={form.type}
                options={[
                  { value: "REST", text: "REST" },
                  { value: "SOAP", text: "SOAP", disabled: true } as any,
                  { value: "GraphQL", text: "GraphQL", disabled: true } as any,
                ]}
                onChange={(v: any) => setTop("type", String(v ?? ""))}
              />
            </group>
          </group>

          <group data-gap="20">
            <group data-fit="1" data-length="forcefit">
              <Select dataLength="autofit" size="large" label={t("format")} name="ds_format" required value={form.format}
                options={[
                  { value: "JSON", text: "JSON" },
                  { value: "XML", text: "XML", disabled: true } as any,
                  { value: "CSV", text: "CSV", disabled: true } as any,
                ]}
                onChange={(v: any) => setTop("format", String(v ?? ""))}
              />
            </group>
            <group data-fit="1" data-length="forcefit">
              <Input dataLength="autofit" size="large" type="text" label="Base URL" name="ds_base" required value={form.baseUrl} onChange={(e: any) => setTop("baseUrl", String(e.target?.value ?? ""))} />
            </group>
          </group>

          <group data-gap="20">
            <group data-fit="1" data-length="forcefit">
              <Select dataLength="autofit" size="large" label={`${t("read")} ${t("method")}`} name="ds_read_method" required value={form.read.method}
                options={[{ value: "GET", text: "GET" }, { value: "POST", text: "POST" }, { value: "PUT", text: "PUT" }]}
                onChange={(v: any) => setRead("method", String(v ?? ""))}
              />
            </group>
            <group data-fit="1" data-length="forcefit" data-direction="column" data-gap="6">
              <group data-wrap="no" data-gap="10" data-align="end">
                <Input dataLength="autofit" size="large" label={`${t("endpoint")}`} type="text" name="ds_read_endpoint" value={form.read.endpoint} onChange={(e: any) => setRead("endpoint", String(e.target?.value ?? ""))} />
              </group>
            </group>
          </group>

          {showTest && (
            <Input
              dataLength="autofit"
              size="large"
              type={InputType.TextArea}
              label="Test result"
              name="ds_test_result"
              value={testResultValue}
            //   rows={12}
            //   disabled
            />
          )}

          <group data-gap="10">
            <group data-fit="1" data-length="forcefit" />
            <Button type="button" primary onClick={onTestRead} disabled={isTesting}>Test</Button>
          </group>

          <group data-direction="column" data-gap="8">
            <Input type={InputType.Checkbox} name="ds_update_active" label={t("update")} checked={form.update.active} onCustomChange={() => toggleSection("update")} />
            {form.update.active && (
              <group data-gap="20">
                <group data-fit="1" data-length="forcefit">
                  <Select dataLength="autofit" size="large" label={t("method")} name="ds_update_method" value={form.update.method}
                    options={[{ value: "PUT", text: "PUT" }, { value: "PATCH", text: "PATCH" }, { value: "POST", text: "POST" }, { value: "GET", text: "GET" }]}
                    onChange={(v: any) => setCrud("update", "method", String(v ?? ""))}
                  />
                </group>
                <group data-fit="1" data-length="forcefit">
                  <Input dataLength="autofit" size="large" type="text" label={t("endpoint")} name="ds_update_endpoint" placeholder="/##id##" value={form.update.endpoint} onChange={(e: any) => setCrud("update", "endpoint", String(e.target?.value ?? ""))} />
                </group>
              </group>
            )}
          </group>

          <group data-direction="column" data-gap="8">
            <Input type={InputType.Checkbox} name="ds_create_active" label={t("create")} checked={form.create.active} onCustomChange={() => toggleSection("create")} />
            {form.create.active && (
              <group data-gap="20">
                <group data-fit="1" data-length="forcefit">
                  <Select dataLength="autofit" size="large" label={t("method")} name="ds_create_method" value={form.create.method}
                    options={[{ value: "POST", text: "POST" }, { value: "GET", text: "GET" }]}
                    onChange={(v: any) => setCrud("create", "method", String(v ?? ""))}
                  />
                </group>
                <group data-fit="1" data-length="forcefit">
                  <Input dataLength="autofit" size="large" type="text" label={t("endpoint")} name="ds_create_endpoint" value={form.create.endpoint} onChange={(e: any) => setCrud("create", "endpoint", String(e.target?.value ?? ""))} />
                </group>
              </group>
            )}
          </group>

          <group data-direction="column" data-gap="8">
            <Input type={InputType.Checkbox} name="ds_trash_active" label={t("trash")} checked={form.trash.active} onCustomChange={() => toggleSection("trash")} />
            {form.trash.active && (
              <group data-gap="20">
                <group data-fit="1" data-length="forcefit">
                  <Select dataLength="autofit" size="large" label={t("method")} name="ds_trash_method" value={form.trash.method}
                    options={[{ value: "DELETE", text: "DELETE" }, { value: "POST", text: "POST" }, { value: "GET", text: "GET" }]}
                    onChange={(v: any) => setCrud("trash", "method", String(v ?? ""))}
                  />
                </group>
                <group data-fit="1" data-length="forcefit">
                  <Input dataLength="autofit" size="large" type="text" label={t("endpoint")} name="ds_trash_endpoint" placeholder="/##id##" value={form.trash.endpoint} onChange={(e: any) => setCrud("trash", "endpoint", String(e.target?.value ?? ""))} />
                </group>
              </group>
            )}
          </group>

          <group data-direction="column" data-gap="8">
            <Input type={InputType.Checkbox} name="ds_distant_active" label={t("distant")} checked={form.distant.active} onCustomChange={() => toggleSection("distant")} />
            {form.distant.active && (
              <group data-direction="column" data-gap="12">
                <group data-gap="20">
                    <group data-fit="1" data-length="forcefit">
                        <Input dataLength="autofit" size="large" type="text" label={t("limit")} name="ds_distant_limit" placeholder="?limit=##limit##" value={form.distant.limit} onChange={(e: any) => setDistant("limit", String(e.target?.value ?? ""))} />
                    </group>
                    <group data-fit="1" data-length="forcefit">
                        <Input dataLength="autofit" size="large" type="text" label={t("skip")} name="ds_distant_skip" placeholder="?skip=##skip##" value={form.distant.skip} onChange={(e: any) => setDistant("skip", String(e.target?.value ?? ""))} />
                    </group>
                </group>
                <group data-gap="20">
                    <group data-fit="1" data-length="forcefit">
                        <Input dataLength="autofit" size="large" type="text" label="Total" name="ds_distant_total" placeholder="/count" value={form.distant.total} onChange={(e: any) => setDistant("total", String(e.target?.value ?? ""))} />
                    </group>
                    <group data-fit="1" data-length="forcefit">
                        <Input dataLength="autofit" size="large" type="text" label={t("sort")} name="ds_distant_sort" placeholder="?sort=##sortfield## ##direction##" value={form.distant.sort} onChange={(e: any) => setDistant("sort", String(e.target?.value ?? ""))} />
                    </group>
                </group>
                <group data-gap="20">
                    <group data-fit="1" data-length="forcefit">
                        <Input dataLength="autofit" size="large" type="text" label={t("where")} name="ds_distant_where" placeholder='?where={"##field##":{"contains":"##value##"}}' value={form.distant.where} onChange={(e: any) => setDistant("where", String(e.target?.value ?? ""))} />
                    </group>
                  <group data-fit="1" data-length="forcefit" />
                </group>
              </group>
            )}
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default DatasourceEditorPopup;
